import React from 'react';
import { useTranslation } from 'react-i18next';
import { IContract } from '../../../../../../types/contract';
import {
  ContractExtractionResult,
  Paragraph,
} from '../../../../../../types/aiOutput';
import { DialogMode } from '../../../../../../types/dialogmode';
import { IPrompt } from '../../../../../../types/prompt';
import { IDocument } from '../../../../../../types/document';
import { HumanDataVerification } from '../../../../../../types/humanDataVerification';
import PromptsSection from './PromptsSection';

interface FieldsFoundTabProps {
  contract: IContract;
  prompts: IPrompt[];
  mode: DialogMode;
  aiOutputs: ContractExtractionResult[];
  selectParagraph: (
    paragraph?: Paragraph,
    prompt?: IPrompt,
    aiOutput?: ContractExtractionResult
  ) => void;
  expanded?: string;
  verifications?: HumanDataVerification[];
  documents: IDocument[];
}

const FieldsFoundTab: React.FC<FieldsFoundTabProps> = ({
  contract,
  prompts,
  mode,
  aiOutputs,
  selectParagraph,
  expanded,
  verifications,
  documents,
}) => {
  const { t } = useTranslation();

  const promptWasFound = (p: IPrompt) => {
    return contract?.prompts?.find((pr) => pr.promptID === p.id)
      ?.extractionResult?.infoFound;
  };

  const promptIsExcluded = (p: IPrompt): boolean => {
    return p.id && contract?.exludedPromptIds?.includes(p.id) ? true : false;
  };

  const promptIsForContractCategory = (p: IPrompt): boolean => {
    // exclude prompts that are assigned to other categories
    let contractCategoryId: string | undefined = undefined;

    if (contract?.contractCategory?.id) {
      contractCategoryId = contract.contractCategory.id;
    } else if (
      aiOutputs?.some((a) => a.metadata?.structuralData?.contractCategory)
    ) {
      contractCategoryId = aiOutputs.filter(
        (a) => a.metadata?.structuralData?.contractCategory
      )[0].metadata?.structuralData?.contractCategory?.id;
    }

    if (p.contractCategoryIds && p.contractCategoryIds?.length > 0) {
      if (contractCategoryId) {
        return p.contractCategoryIds?.some((c) => c === contractCategoryId);
      }

      // the contract has no category and the prompt is for another category
      return false;
    }

    return true;
  };

  const filteredPrompts = prompts
    .filter((p) => promptIsForContractCategory(p))
    .filter(
      (p) => p.enabled && !p.hidden && promptWasFound(p) && !promptIsExcluded(p)
    )
    .sort((a, b) => {
      // sort on the translated title
      const aCategory = a.title ? t(a.title)?.toLowerCase() : '';
      const bCategory = b.title ? t(b.title)?.toLowerCase() : '';

      if (aCategory < bCategory) {
        return -1;
      }
      if (aCategory > bCategory) {
        return 1;
      }
      return 0;
    });

  return (
    <PromptsSection
      contract={contract}
      prompts={filteredPrompts}
      mode={mode}
      aiOutputs={aiOutputs}
      selectParagraph={selectParagraph}
      expanded={expanded}
      infoFound={true}
      verifications={verifications}
      documents={documents}
    />
  );
};

export default FieldsFoundTab;
